import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  Directive,
  effect,
  inject,
  input,
  OnDestroy,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { map, type Subscription } from 'rxjs';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import type { ListResponse } from '../services/api-resource/resource.types';
import { TeamMembersPreviewComponent, type TeamMemberAssignment } from '../team-members-preview/team-members-preview.component';

const HOVER_DEBOUNCE_MS = 200;
const CLOSE_GRACE_MS = 300;

interface TeamPersonsPageItem {
  personId: string;
  role: string;
}

@Component({
  selector: 'zag-team-members-popover-content',
  template: `
    <ng-template #popoverTemplate>
      <zag-team-members-preview
        [assignments]="assignments()"
        [loading]="loading()"
        [loadError]="loadError()"
        [labels]="labels()"
        [avatarUrls]="avatarUrls()"
      />
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TeamMembersPreviewComponent],
})
export class TeamMembersPopoverContent {
  readonly assignments = input<TeamMemberAssignment[]>([]);
  readonly loading = input(false);
  readonly loadError = input(false);
  readonly labels = input<Map<string, string> | null>(null);
  readonly avatarUrls = input<Map<string, string | null> | null>(null);
  readonly popoverTemplate = viewChild<TemplateRef<unknown>>('popoverTemplate');
}

@Directive({
  selector: '[zagTeamMembersPopover]',
  hostDirectives: [TooltipDirective],
  host: {
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
    '(focus)': 'onFocus()',
    '(blur)': 'onBlur()',
  },
})
export class TeamMembersPopoverDirective implements OnDestroy {
  private readonly tooltip = inject(TooltipDirective, { self: true });
  private readonly http = inject(HttpClient);
  private readonly viewContainer = inject(ViewContainerRef);

  readonly zagTeamMembersPopover = input.required<string>();
  readonly teamMembersLabels = input<Map<string, string> | null>(null);
  readonly teamMembersAvatarUrls = input<Map<string, string | null> | null>(null);

  private readonly cache = new Map<string, TeamMemberAssignment[]>();
  private contentRef: ComponentRef<TeamMembersPopoverContent> | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private closeGraceTimer: ReturnType<typeof setTimeout> | null = null;
  private fetchSubscription: Subscription | null = null;
  private requestId = 0;
  private hovered = false;
  private focused = false;
  private lastTeam = '';

  constructor() {
    this.contentRef = this.viewContainer.createComponent(TeamMembersPopoverContent);
    this.contentRef.changeDetectorRef.detectChanges();

    let initial = true;
    effect(() => {
      const teamId = this.zagTeamMembersPopover();
      if (initial) {
        initial = false;
        return;
      }
      if ((!this.hovered && !this.focused) || this.debounceTimer !== null) return;
      this.cancelFetch();
      this.load(teamId);
    });
    effect(() => {
      const content = this.contentRef;
      if (!content) return;
      content.setInput('labels', this.teamMembersLabels());
      content.setInput('avatarUrls', this.teamMembersAvatarUrls());
    });
  }

  ngOnDestroy(): void {
    this.hovered = false;
    this.focused = false;
    this.clearDebounce();
    this.clearCloseGrace();
    this.cancelFetch();
    this.tooltip.hideImmediately();
    this.tooltip.setContent(null);
    this.contentRef?.destroy();
    this.contentRef = null;
  }

  onMouseEnter(): void {
    this.hovered = true;
    this.scheduleOpen();
  }

  onMouseLeave(): void {
    this.hovered = false;
    if (!this.focused) this.close();
  }

  onFocus(): void {
    this.focused = true;
    this.scheduleOpen();
  }

  onBlur(): void {
    this.focused = false;
    if (!this.hovered) this.close();
  }

  private scheduleOpen(): void {
    this.clearDebounce();
    this.clearCloseGrace();
    if (this.zagTeamMembersPopover() !== this.lastTeam) {
      this.tooltip.setContent(null);
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.load(this.zagTeamMembersPopover());
    }, HOVER_DEBOUNCE_MS);
  }

  private close(): void {
    this.clearDebounce();
    this.cancelFetch();
    this.clearCloseGrace();
    this.closeGraceTimer = setTimeout(() => {
      this.closeGraceTimer = null;
      if (!this.tooltip.panelHovered()) {
        this.tooltip.hide();
      }
    }, CLOSE_GRACE_MS);
  }

  private load(teamId: string): void {
    if (!teamId) {
      this.tooltip.hideImmediately();
      return;
    }
    this.lastTeam = teamId;
    const cached = this.cache.get(teamId);
    if (cached) {
      this.show(cached, false, false);
      return;
    }
    this.show([], true, false);
    this.cancelFetch();
    const currentRequest = ++this.requestId;
    this.fetchSubscription = this.http
      .get<TeamPersonsPageItem[] | ListResponse<TeamPersonsPageItem>>(`/api/v1/planning/teams/${teamId}/persons?take=200`)
      .pipe(map(res => (Array.isArray(res) ? res : res.data)))
      .subscribe({
        next: assignments => {
          if (currentRequest !== this.requestId) return;
          this.fetchSubscription = null;
          this.cache.set(teamId, assignments);
          this.show(assignments, false, false);
        },
        error: () => {
          if (currentRequest !== this.requestId) return;
          this.fetchSubscription = null;
          this.show([], false, true);
        },
      });
  }

  private show(assignments: TeamMemberAssignment[], loading: boolean, loadError: boolean): void {
    const content = this.contentRef;
    if (!content) return;
    content.setInput('assignments', assignments);
    content.setInput('loading', loading);
    content.setInput('loadError', loadError);
    content.setInput('labels', this.teamMembersLabels());
    content.setInput('avatarUrls', this.teamMembersAvatarUrls());
    this.tooltip.setContent(content.instance.popoverTemplate() ?? null);
    this.tooltip.show();
  }

  private clearDebounce(): void {
    if (this.debounceTimer === null) return;
    clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
  }

  private clearCloseGrace(): void {
    if (this.closeGraceTimer === null) return;
    clearTimeout(this.closeGraceTimer);
    this.closeGraceTimer = null;
  }

  private cancelFetch(): void {
    this.requestId++;
    this.fetchSubscription?.unsubscribe();
    this.fetchSubscription = null;
  }
}
